import { useAtomValue } from 'jotai';
import { FC, useMemo, useState } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { Loader } from '../../../components/Loader/Loader';
import { companyMetricsLoadableJ } from '../../../services/state/CompanyMetricsStateJ';
import { portcosByIdMapAtomJ, selectedCompanyIdStateFinanceJ } from '../../../services/state/PortCosStateJ';
import { ErrorOccurredMessage } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { ExportCashFlowsDialog } from '../AllTransactions/ExportCashFlowsDialog';
import { CashFlowsAlertBanner } from '../CashFlows/CashFlowsAlertBanner';
import { defaultColDef } from '../common-grid-defs/defaultColDefs';
import { filterOutSecurityDataTemp, SecurityAggFuncs, useSecurityUtils } from './securityUtils';
import { useLedgerColDefs } from './useLedgerColDefs';

export const LedgerGrid: FC = () => {
  const companyId = useAtomValue(selectedCompanyIdStateFinanceJ);
  const portCoMap = useAtomValue(portcosByIdMapAtomJ);
  const company = portCoMap.get(companyId ?? -1);

  const [showExportDialog, setShowExportDialog] = useState(false);

  const companyMetricsLoadable = useAtomValue(companyMetricsLoadableJ({ companyId: companyId ?? -1 }));
  const { securityProcessCellCallback, autoGroupColumnDef } = useSecurityUtils();

  const transactions = useMemo(() => {
    const data = companyMetricsLoadable.state === 'hasData' ? companyMetricsLoadable.data : undefined;
    return filterOutSecurityDataTemp(data?.metrics?.at(0)?.transactions ?? []);
  }, [companyMetricsLoadable]);

  const columnDefs = useLedgerColDefs();
  const defaultColDefs = useMemo(() => ({ ...defaultColDef, minWidth: 150 }), []);
  const exportParams = useMemo(
    () => ({ processCellCallback: securityProcessCellCallback }),
    [securityProcessCellCallback]
  );

  if (companyMetricsLoadable.state === 'loading') {
    return <Loader height='80%' />;
  }
  if (companyMetricsLoadable.state === 'hasError') {
    return <ErrorOccurredMessage />;
  }

  return (
    <>
      {companyId && company && (
        <CashFlowsAlertBanner
          companyId={companyId}
          companyName={company.name}
          onExportClick={() => setShowExportDialog(true)}
        />
      )}

      <FadeInGridWrapper key={companyId}>
        <div style={{ height: '70vh', paddingTop: '2rem' }}>
          <AgTable
            key={companyId}
            columnDefs={columnDefs}
            rowData={transactions}
            defaultColDef={defaultColDefs}
            defaultExcelExportParams={exportParams}
            defaultCsvExportParams={exportParams}
            groupDefaultExpanded={2}
            autoGroupColumnDef={autoGroupColumnDef}
            aggFuncs={SecurityAggFuncs}
            suppressAggFuncInHeader
            grandTotalRow='bottom'
          />
        </div>
      </FadeInGridWrapper>

      <ExportCashFlowsDialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        preSelectedCompanyId={companyId ?? undefined}
      />
    </>
  );
};
